import { z } from 'zod';
import { BankAccountSchema, BANK_ACCOUNT_NUMBER_MIN_LENGTH, BANK_ACCOUNT_NUMBER_MAX_LENGTH } from './account.js';
import { nullableText } from '../sanitised-text.js';
import { BankAccountTypeSchema } from '../bank.js';
// =============================================================================
// Supplier bank account
// =============================================================================
//
// A supplier's payment destination as a first-class, correctable resource -
// the counterpart to the firm's own `/accounts/:accountId/bank-accounts` for
// a `BankAccount` hanging off a SUPPLIER's business entity rather than the
// firm's. See the `BankAccount` model docblock in `bank-account.prisma` for
// the shared-model reasoning. The response reuses `BankAccountSchema`
// unchanged (including `bankAccountType`) - only the write shapes below are
// new, and they diverge from `CreateBankAccountInputSchema` /
// `UpdateBankAccountInputSchema` in ways a supplier account needs and the
// firm's own never does:
//
//   - the branch code is optional and NOT regex-validated here. It is
//     sanitised server-side with `sanitiseBankBranchCode`, which drops
//     anything that is not six digits rather than rejecting the whole write -
//     invoices routinely print an account with no branch code at all
//     (universal branch codes make it redundant). The firm's own
//     `CreateBankAccountInputSchema` keeps the strict 6-digit requirement
//     unchanged: a firm account cannot be reconciled without it.
//   - `currency` is accepted on create. The caller (typically transcribing a
//     supplier invoice) knows the source document's currency; defaulting a
//     foreign account to ZAR would mislabel it. `isDefault` stays per-entity,
//     so a supplier holding both a ZAR and a USD account has no single
//     "primary" - deliberately unresolved here, not silently assumed.
//
// There is no `kind` field: a supplier account can never be a TRUST account
// (rejected in `bank-account.service.ts` `createBankAccount`), so the field
// would only ever carry one value on this resource.
export const CreateSupplierBankAccountInputSchema = z.object({
    label: z.string().min(1, 'Label is required').max(100, 'Label must be 100 characters or less'),
    bankName: nullableText(100),
    bankBranchCode: nullableText(20),
    bankAccountNumber: z.string()
        .min(BANK_ACCOUNT_NUMBER_MIN_LENGTH, `Account number must be at least ${BANK_ACCOUNT_NUMBER_MIN_LENGTH} digits`)
        .max(BANK_ACCOUNT_NUMBER_MAX_LENGTH, `Account number must be at most ${BANK_ACCOUNT_NUMBER_MAX_LENGTH} digits`),
    bankAccountHolderName: nullableText(200),
    bankAccountType: BankAccountTypeSchema.nullable().optional(),
    /**
     * ISO 4217 code, e.g. "USD". Omitted means "no currency asserted" on the
     * wire - the BankAccount.currency column, not this schema, is what
     * defaults it to ZAR, so an omitted value has one meaning stored in one
     * place.
     */
    currency: z.string().regex(/^[A-Z]{3}$/, 'Currency must be a 3-letter ISO 4217 code (e.g. ZAR)').optional(),
    /**
     * Explicit opt-in only - never inferred from anything else on the request.
     * The service still forces the FIRST account on a supplier's entity to be
     * default (there is nothing yet to displace); this flag governs every
     * account after that. A document must never silently reassign which
     * account we pay - that is the fraud invariant this whole feature exists
     * to hold.
     */
    isDefault: z.boolean().optional(),
    idempotencyKey: z.string().min(1),
});
export const UpdateSupplierBankAccountInputSchema = z.object({
    label: z.string().min(1, 'Label is required').max(100).optional(),
    bankName: nullableText(100),
    bankBranchCode: nullableText(20),
    bankAccountNumber: z.string().min(BANK_ACCOUNT_NUMBER_MIN_LENGTH).max(BANK_ACCOUNT_NUMBER_MAX_LENGTH).nullable().optional(),
    bankAccountHolderName: nullableText(200),
    bankAccountType: BankAccountTypeSchema.nullable().optional(),
    isDefault: z.boolean().optional(),
});
// Flat array, not paginated - mirrors the inline `{ data: z.array(...) }`
// shape the firm's own `GET /accounts/:accountId/bank-accounts` route uses
// (`routes/bank-account.ts`), not `BankAccountListResponseSchema` (that one
// is `createPaginatedResponseSchema(AccountOwnedBankAccountSchema)` and has no route
// consumer - the firm's list route never adopted it). A bank account list is
// a per-entity dataset small enough that pagination would add nothing.
export const SupplierBankAccountListResponseSchema = z.object({ data: z.array(BankAccountSchema) });
